/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import { CityPropType, NewBookingDeliveryPropType } from 'common/prop-types';
import { newBookingOverlayType } from 'constants/new-booking-constants';
import { SCRIBE_STATE_NAMES } from 'applications/scribe/instrumentations/track';
import React, { useCallback, useEffect, useMemo } from 'react';
import cx from 'classnames';
import { Col, Icon, Row } from 'grab-ui';
import pickUpIcon from 'public/images/pick-up.svg';
import dropOffIcon from 'public/images/drop-off.svg';
import _get from 'lodash/get';
import { connect } from 'react-redux';
import {
  deliveryDetailOverlayVisibleSelector,
  firstDeliverySelector,
  allDeliveriesSelector,
  prefillSenderInfoSelector,
} from 'selectors/new-booking-selectors';
import { selectedCitySelector } from 'selectors/app-selector';
import NewBookingDuck from 'ducks/new-booking-duck';
import GeoDuck from 'ducks/geo-duck';
import NewHistoryDuck from 'ducks/new-history-duck';
import ScribeDuck from 'ducks/scribe-duck';
import { isEmptyAllDeliveries } from 'helper/anytime-booking';

import { ServiceConfigurationField } from '../common';
import styles from './styles.less';
import CTAButton from './cta-button';
import useUnloadConfirm from '../common/use-unload-confirm';

const HomepageWidget = ({
  dispatch,
  city,
  delivery,
  detailOverlayVisible,
  allDeliveries,
  prefillSenderInfo,
}) => {
  const { id: deliveryID, detail: deliveryDetail } = delivery;

  useEffect(
    () => {
      if (!deliveryID) {
        dispatch(NewBookingDuck.creators.createNewDelivery());
      }
    },
    [dispatch, deliveryID],
  );

  useEffect(
    () => {
      if (deliveryID) {
        dispatch(
          GeoDuck.creators.fetchDefaultPickUpStepForDelivery(deliveryID),
        );
        dispatch(NewHistoryDuck.creators.fetchLastCompletedOrder());
      }
    },
    [deliveryID],
  );

  useEffect(
    () => {
      const pickUp = _get(deliveryDetail, 'pickUp', {});
      if (prefillSenderInfo && deliveryID && !pickUp.name && !pickUp.phone) {
        dispatch(
          NewBookingDuck.creators.setPickUp(
            deliveryID,
            {
              name: prefillSenderInfo.name,
              phone: prefillSenderInfo.phone,
              locationNote: prefillSenderInfo.instruction,
            },
            pickUp.source,
          ),
        );
      }
    },
    [prefillSenderInfo, deliveryID, deliveryDetail, dispatch],
  );

  const allDeliveriesEmpty = useMemo(
    () => isEmptyAllDeliveries(allDeliveries),
    [allDeliveries],
  );
  useUnloadConfirm(allDeliveriesEmpty);

  const swapDisabled = useMemo(
    () => _get(deliveryDetail, 'dropOffs.length', 0) > 1,
    [deliveryDetail],
  );

  const dropOffDisplay = useMemo(
    () => {
      const dropOffs = _get(deliveryDetail, 'dropOffs', []).filter(
        dropOff => dropOff && dropOff.poi,
      );
      switch (dropOffs.length) {
        case 0:
          return I18n.t(
            'v2.homepage.manualOrder.placeholder.recipientLocation',
          );
        case 1:
          return _get(dropOffs[0], 'poi.address.name');
        default:
          return I18n.t(
            'v2.homepage.manualOrder.placeholder.recipientLocationAndMore',
            {
              firstAddress: _get(dropOffs[0], 'poi.address.name'),
              remainingCount: dropOffs.length - 1,
            },
          );
      }
    },
    [deliveryDetail],
  );

  const swapDeliveryPickUpDropOff = useCallback(
    e => {
      if (e) {
        e.stopPropagation(); // prevent click event to show location drawer
      }
      if (deliveryID) {
        dispatch(NewBookingDuck.creators.swapDeliveryPickUpDropOff(deliveryID));
        dispatch(
          ScribeDuck.creators.scribeExpressHomePageSwapDeliveryPickUpDropOff(
            deliveryDetail,
          ),
        );
      }
    },
    [deliveryID, deliveryDetail],
  );

  const detailOverlayHandler = useCallback(
    overlayType => () => {
      if (deliveryID) {
        dispatch(
          NewBookingDuck.creators.openDetailOverlay(deliveryID, overlayType),
        );
        if (overlayType === newBookingOverlayType.serviceConfiguration) {
          dispatch(ScribeDuck.creators.scribeExpressHomePageWebServiceType());
        }
        if (overlayType === newBookingOverlayType.dropOffAddress) {
          dispatch(
            ScribeDuck.creators.scribeExpressHomePageWebDropoffAddress(
              deliveryDetail,
            ),
          );
        }
        if (overlayType === newBookingOverlayType.pickUpAddress) {
          dispatch(
            ScribeDuck.creators.scribeExpressHomePageWebPickupAddress(
              deliveryDetail,
            ),
          );
        }
      }
    },
    [deliveryID, deliveryDetail],
  );

  if (!deliveryID) {
    return null;
  }

  return (
    <Row className={styles.bookOrderWidget} type="flex">
      <Col xs={22} md={6} className={cx(styles.poiField, styles.pickUp)}>
        <div
          className={styles.location}
          onClick={detailOverlayHandler(newBookingOverlayType.pickUpAddress)}
        >
          <img src={pickUpIcon} alt="" />
          <h6>
            {_get(
              deliveryDetail,
              'pickUp.poi.address.name',
              I18n.t('v2.homepage.manualOrder.placeholder.senderLocation'),
            )}
          </h6>
        </div>
      </Col>
      <Col xs={2} md={1} className={styles.swapField}>
        <span className={styles.swapIcon}>
          <Icon
            className={cx({ [styles.swapIconDisabled]: swapDisabled })}
            type="swap"
            onClick={swapDisabled ? undefined : swapDeliveryPickUpDropOff}
          />
        </span>
      </Col>
      <Col xs={24} md={6} className={cx(styles.poiField, styles.dropOff)}>
        <div
          className={styles.location}
          onClick={detailOverlayHandler(newBookingOverlayType.dropOffAddress)}
        >
          <img src={dropOffIcon} alt="" />
          <h6> {dropOffDisplay} </h6>
        </div>
      </Col>
      <Col xs={24} md={7}>
        <ServiceConfigurationField
          className={styles.serviceConfigField}
          delivery={delivery}
          onClick={detailOverlayHandler(
            newBookingOverlayType.serviceConfiguration,
          )}
          stateName={SCRIBE_STATE_NAMES.EXPRESS_HOMEPAGE_WEB}
        />
      </Col>
      <CTAButton
        delivery={delivery}
        city={city}
        detailOverlayVisible={detailOverlayVisible}
        dispatch={dispatch}
      />
    </Row>
  );
};

HomepageWidget.propTypes = {
  city: CityPropType,
  delivery: NewBookingDeliveryPropType,
  dispatch: PropTypes.func.isRequired,
  detailOverlayVisible: PropTypes.bool,
  allDeliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
  prefillSenderInfo: PropTypes.shape({
    name: PropTypes.string,
    phone: PropTypes.string,
    instruction: PropTypes.string,
  }),
};

HomepageWidget.defaultProps = {
  city: {},
  delivery: {},
  detailOverlayVisible: false,
  prefillSenderInfo: undefined,
};

export default connect(state => ({
  city: selectedCitySelector(state),
  delivery: firstDeliverySelector(state),
  detailOverlayVisible: deliveryDetailOverlayVisibleSelector(state),
  allDeliveries: allDeliveriesSelector(state),
  prefillSenderInfo: prefillSenderInfoSelector(state),
}))(HomepageWidget);
